import React from 'react';
import {
    Box,
    Typography,
    Paper,
    LinearProgress,
    Chip,
    Grid,
    Card,
    CardContent,
    Stack
} from '@mui/material';
import { TrendingUp, Star, NavigateNext } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

const LevelProgressCard = ({ userStats }) => {
    if (!userStats || !userStats.currentLevel) return null;

    const { currentLevel, nextLevel, progressPercentage, totalPoints } = userStats;
    const isMaxLevel = !nextLevel;

    return (
        <Paper elevation={8} sx={{
            borderRadius: 4,
            overflow: 'hidden',
            mb: 4,
            background: `linear-gradient(135deg, ${alpha(currentLevel.color, 0.15)}, rgba(255,255,255,0.95))`,
            border: `3px solid ${alpha(currentLevel.color, 0.3)}`
        }}>
            <Box sx={{
                background: `linear-gradient(90deg, ${currentLevel.color}, ${alpha(currentLevel.color, 0.8)})`,
                color: 'white',
                p: 2,
                textAlign: 'center'
            }}>
                <Typography variant="h5" fontWeight={800}>
                    🎯 Level Progress & Advancement
                </Typography>
            </Box>

            <Box sx={{ p: 4 }}>
                <Grid container spacing={4} alignItems="center">
                    {/* Current Level */}
                    <Grid item xs={12} md={4}>
                        <Card elevation={4} sx={{
                            textAlign: 'center',
                            background: `linear-gradient(135deg, ${currentLevel.color}, ${alpha(currentLevel.color, 0.8)})`,
                            color: 'white',
                            borderRadius: 3
                        }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h2" sx={{ mb: 1 }}>
                                    {currentLevel.icon}
                                </Typography>
                                <Typography variant="h4" fontWeight={800}>
                                    {currentLevel.name}
                                </Typography>
                                <Typography variant="body1" sx={{ opacity: 0.9, mt: 1 }}>
                                    Current Level
                                </Typography>
                                <Chip
                                    icon={<Star />}
                                    label={`${totalPoints} Points`}
                                    sx={{
                                        mt: 2,
                                        background: 'rgba(255,255,255,0.2)',
                                        color: 'white',
                                        fontWeight: 700
                                    }}
                                />
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Progress Section */}
                    <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                            {!isMaxLevel ? (
                                <>
                                    <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: '#1b5e20' }}>
                                        Progress to {nextLevel.name}
                                    </Typography>

                                    <Box sx={{ position: 'relative', mb: 3 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={progressPercentage}
                                            sx={{
                                                height: 20,
                                                borderRadius: 10,
                                                backgroundColor: alpha(currentLevel.color, 0.2),
                                                border: `2px solid ${alpha(currentLevel.color, 0.3)}`,
                                                '& .MuiLinearProgress-bar': {
                                                    background: `linear-gradient(90deg, ${currentLevel.color}, ${nextLevel.color})`,
                                                    borderRadius: 10,
                                                    boxShadow: `0 4px 12px ${alpha(currentLevel.color, 0.4)}`
                                                }
                                            }}
                                        />
                                        <Typography
                                            variant="caption"
                                            fontWeight={800}
                                            sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                color: 'white',
                                                textShadow: '0 1px 3px rgba(0,0,0,0.7)',
                                                fontSize: '0.9rem'
                                            }}
                                        >
                                            {Math.round(progressPercentage)}%
                                        </Typography>
                                    </Box>

                                    <Typography variant="body1" fontWeight={600} color="text.secondary">
                                        {nextLevel.minPoints - totalPoints} points needed
                                    </Typography>

                                    <NavigateNext sx={{
                                        fontSize: 40,
                                        color: currentLevel.color,
                                        animation: 'pulse 2s infinite',
                                        mt: 1
                                    }} />
                                </>
                            ) : (
                                <Box>
                                    <Typography variant="h4" sx={{ color: '#ffd700', mb: 2 }}>
                                        👑
                                    </Typography>
                                    <Typography variant="h5" fontWeight={800} sx={{ color: '#ffd700', mb: 1 }}>
                                        Maximum Level!
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        You've reached the pinnacle of eco excellence!
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Grid>

                    {/* Next Level */}
                    <Grid item xs={12} md={4}>
                        {!isMaxLevel ? (
                            <Card elevation={4} sx={{
                                textAlign: 'center',
                                background: `linear-gradient(135deg, ${nextLevel.color}, ${alpha(nextLevel.color, 0.8)})`,
                                color: 'white',
                                borderRadius: 3,
                                opacity: 0.8,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    opacity: 1,
                                    transform: 'translateY(-5px)'
                                }
                            }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h2" sx={{ mb: 1 }}>
                                        {nextLevel.icon}
                                    </Typography>
                                    <Typography variant="h4" fontWeight={800}>
                                        {nextLevel.name}
                                    </Typography>
                                    <Typography variant="body1" sx={{ opacity: 0.9, mt: 1 }}>
                                        Next Level
                                    </Typography>
                                    <Chip
                                        icon={<TrendingUp />}
                                        label={`${nextLevel.minPoints} Points`}
                                        sx={{
                                            mt: 2,
                                            background: 'rgba(255,255,255,0.2)',
                                            color: 'white',
                                            fontWeight: 700
                                        }}
                                    />
                                </CardContent>
                            </Card>
                        ) : (
                            <Card elevation={4} sx={{
                                textAlign: 'center',
                                background: 'linear-gradient(135deg, #ffd700, #ffb300)',
                                color: 'white',
                                borderRadius: 3
                            }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h2" sx={{ mb: 1 }}>
                                        🎉
                                    </Typography>
                                    <Typography variant="h4" fontWeight={800}>
                                        Legendary!
                                    </Typography>
                                    <Typography variant="body1" sx={{ opacity: 0.9, mt: 1 }}>
                                        Ultimate Achievement
                                    </Typography>
                                    <Chip
                                        label="Max Level"
                                        sx={{
                                            mt: 2,
                                            background: 'rgba(255,255,255,0.2)',
                                            color: 'white',
                                            fontWeight: 700
                                        }}
                                    />
                                </CardContent>
                            </Card>
                        )}
                    </Grid>
                </Grid>

                {/* Level Benefits Preview */}
                {!isMaxLevel && (
                    <Box sx={{ mt: 4, p: 3, borderRadius: 3, background: alpha(nextLevel.color, 0.1) }}>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: nextLevel.color, textAlign: 'center' }}>
                            🎁 Unlock at {nextLevel.name} Level
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center">
                            <Chip label="Enhanced Profile Themes" size="small" />
                            <Chip label="Exclusive Badge Access" size="small" />
                            <Chip label="Advanced Statistics" size="small" />
                            <Chip label="Leaderboard Highlight" size="small" />
                            {nextLevel.name === 'Master Sorter' && (
                                <Chip label="Mentor Status" size="small" sx={{ background: '#ffc400', color: 'white' }} />
                            )}
                            {nextLevel.name === 'Eco-Legend' && (
                                <Chip label="Hall of Fame" size="small" sx={{ background: '#ff5722', color: 'white' }} />
                            )}
                        </Stack>
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

export default LevelProgressCard;
